import { useState, useEffect } from 'react'
import client from '../api/client'
import { TapDashboard } from '../types/index'

interface UseDashboardReturn {
  dashboard: TapDashboard | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

// Backend serialises DashboardSummary in snake_case (total_applications,
// by_stage, etc.). The frontend TapDashboard type is camelCase, so map once
// here and every consumer gets a consistent shape.
interface RawDashboard {
  total_applications?: number | null
  by_stage?: Record<string, number> | null
  by_vacancy_code?: Record<string, number> | null
  avg_time_in_stage?: Record<string, number> | null
  conversion_rates?: Record<string, number> | null
  source_breakdown?: Record<string, number> | null
  avg_time_to_hire?: number | null
}

const mapDashboard = (raw: RawDashboard): TapDashboard => ({
  totalApplications: raw.total_applications ?? 0,
  byStage: raw.by_stage ?? {},
  byVacancyCode: raw.by_vacancy_code ?? {},
  avgTimeInStage: raw.avg_time_in_stage ?? {},
  conversionRates: raw.conversion_rates ?? {},
  sourceBreakdown: raw.source_breakdown ?? {},
  avgTimeToHire: raw.avg_time_to_hire ?? undefined,
})

export const useDashboard = (): UseDashboardReturn => {
  const [dashboard, setDashboard] = useState<TapDashboard | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await client.get<RawDashboard>('/tap/dashboard')
      setDashboard(mapDashboard(response.data || {}))
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch dashboard')
      setError(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refetch()
  }, [])

  return { dashboard, isLoading, error, refetch }
}
